import React, { useState } from 'react';
import styled from 'styled-components';
import MediaPlayer from './MediaPlayer';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

// ── Detecta URLs en texto y las convierte en <a> clicables ──────────────────
const URL_REGEX = /(https?:\/\/[^\s]+)/g;

const renderDescription = (text, isDark) => {
  if (!text) return <DescriptionEmpty>Sin descripción disponible.</DescriptionEmpty>;

  return text.split('\n').map((line, lineIdx) => {
    // Divide cada línea por URLs
    const parts = line.split(URL_REGEX);
    return (
      <span key={lineIdx}>
        {parts.map((part, partIdx) =>
          URL_REGEX.test(part) ? (
            <DescLink
              key={partIdx}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              isDark={isDark}
            >
              {part}
            </DescLink>
          ) : (
            part
          )
        )}
        <br />
      </span>
    );
  });
};

const ProductDetailView = ({ content, theme, onBack, token, images, isPublicPage, userId, isEmbedded }) => {
  const isDarkMode = theme === 'dark';
  const { slug } = useParams();
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);
  const CHAR_LIMIT = 400;
  const shouldTruncate = content.description?.length > CHAR_LIMIT;

  // Lógica para decidir qué texto mostrar
  const textToShow = (shouldTruncate && !isExpanded) 
    ? content.description.substring(0, CHAR_LIMIT) + "..." 
    : content.description;

  const API_URL = process.env.REACT_APP_API_URL;

  const getFullImageUrl = () => {
    const imgSrc = (images && images[content._id]) || content.image;
    if (!imgSrc) return `${window.location.origin}/logo192.png`; 
    return imgSrc.startsWith('http') ? imgSrc : `${API_URL}/${imgSrc}`;
  };

  const metaImage = getFullImageUrl();
  const params = new URLSearchParams(window.location.search);
  const storeParam = slug || params.get('store');
  const storeQuery = storeParam ? `?store=${storeParam}` : '';
  const shareUrl = `${API_URL}/api/user/og/content/${content._id}${storeQuery}`;
  // OG URL: el backend sirve HTML con metas estáticas para que WhatsApp/Telegram
  // puedan leer el título, descripción e imagen del contenido sin ejecutar JS.
  // const ogUrl = `${API_URL}/api/user/og/content/${content._id}`;

  const shareText = `¡Mira este contenido: ${content.title}!`;
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    url: shareUrl,
  };

  const canDownload = () => {
    if (content.type === 'video') return false;
    if (!content.isDigitalProduct) return true;
    const isCreator = String(content.userId?._id || content.userId) === String(userId);
    if (isCreator) return true;
    const isRevoked = Array.isArray(content.revokedUsers) &&
      content.revokedUsers.some(u => String(u._id || u) === String(userId));
    if (isRevoked) return false;
    const isAssigned = Array.isArray(content.assignedTo) &&
      content.assignedTo.some(a => String(a._id || a) === String(userId));
    if (isAssigned) return true;
    return Array.isArray(content.downloadAllowedUsers) &&
      content.downloadAllowedUsers.some(u => String(u._id || u) === String(userId));
  };

  const getGalleryUrl = (img) => {
    if (!img) return '';
    return img.startsWith('http') ? img : `${API_URL}/${img}`;
  };

  if (!content) return null;

  const renderMedia = () => {
    const fileStr = Array.isArray(content.file) ? content.file[0] : content.file;
    const fileLower = fileStr ? fileStr.toLowerCase() : '';

    if (activeImage) {
      return <img src={activeImage} alt={content.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />;
    }

    const isVideo = fileLower.endsWith('.mp4') || fileLower.endsWith('.mov') || fileLower.endsWith('.webm') || content.type === 'video';
    const isAudio = fileLower.endsWith('.mp3') || fileLower.endsWith('.wav') || fileLower.endsWith('.m4a');

    if (content.type === 'video' && content.videoUrl && content.videoUrl.includes('youtube')) {
      const videoId = new URL(content.videoUrl).searchParams.get('v');
      return (
        <iframe
          width="100%" height="100%"
          src={`https://www.youtube.com/embed/${videoId}`}
          title={content.title} frameBorder="0" allowFullScreen
        />
      );
    }

    if (isVideo) {
      return <MediaPlayer mediaUrl={fileStr} mediaType="video" isLocalFile={true} token={token} id={content._id} />;
    }

    if (isAudio) {
      return <MediaPlayer mediaUrl={fileStr} mediaType="audio" isLocalFile={true} token={token} id={content._id} />;
    }

    if (content.isDigitalProduct) {
      const imgField = content.image || '';
      const imgLower = imgField.toLowerCase();
      const isVideoPortada = imgLower.endsWith('.mp4') || imgLower.endsWith('.mov');

      if (isVideoPortada) {
        return <MediaPlayer mediaUrl={imgField} mediaType="video" isLocalFile={true} token={token} id={content._id} isCover={true} />;
      }

      const imgSrc = (images && images[content._id]) || imgField;
      if (imgSrc) return <img src={imgSrc} alt={content.title} />;
    }

    const imgSrc = (images && images[content._id]) || content.image;
    return <img src={imgSrc} alt={content.title} onError={(e) => { e.target.src = require('../assets/img/simbolo-de-imagen-n.png'); }} />;
  };

  const renderPrincipalThumb = () => {
    if (content.type === 'imagen') {
      const fileStr = Array.isArray(content.file) ? content.file[0] : content.file;
      const isVideo = fileStr && fileStr.toLowerCase().endsWith('.mp4');
      const isAudio = fileStr && fileStr.toLowerCase().endsWith('.mp3');
      if (isVideo || isAudio) {
        return (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#222', fontSize: '24px', borderRadius: '6px' }}>
            {isVideo ? '🎬' : '🎵'}
          </div>
        );
      }
      const imgSrc = (images && images[content._id]) || content.image;
      return <img src={imgSrc} alt="principal" />;
    }

    if (content.isDigitalProduct) {
      const imgField = content.image || '';
      const isVideo = imgField.toLowerCase().endsWith('.mp4');
      const isAudio = imgField.toLowerCase().endsWith('.mp3');
      if (isVideo || isAudio) {
        return (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#222', fontSize: '24px', borderRadius: '6px' }}>
            {isVideo ? '🎬' : '🎵'}
          </div>
        );
      }
      const imgSrc = (images && images[content._id]) || imgField;
      return <img src={imgSrc} alt="principal" />;
    }

    const imgSrc = (images && images[content._id]) || content.image;
    return imgSrc ? <img src={imgSrc} alt="principal" /> : <div style={{ background: '#222', width: '100%', height: '100%', borderRadius: '6px' }} />;
  };

  const handleShare = (e) => {
    e.stopPropagation();
    setShareMenuOpen(prev => !prev);
  };

  const downloadFile = async () => {
    if (!canDownload()) return;
    try {
      const fileStr = Array.isArray(content?.file) ? content.file[0] : content?.file;
      const extension = fileStr ? fileStr.substring(fileStr.lastIndexOf('.')) : '';
      const cleanFileName = content?.title
        ? `${content.title.replace(/[/\\?%*:|"<>]/g, '-')}${extension}`
        : fileStr
          ? fileStr.replace(/^\d+_/, '').replace(/^\d+\.\s?/, '')
          : `archivo_${content._id}${extension}`;

      const response = await axios.get(`${API_URL}/api/user/download-content/${content._id}`, {
        responseType: 'blob',
        timeout: 50000,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = cleanFileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Ha ocurrido un error al descargar el archivo.');
    }
  };

  return (
    <>
      <Helmet>
        <title>{content.title} | Edeli Store</title>
        <meta property="og:title" content={content.title} />
        <meta property="og:description" content={content.description || 'Descubre este contenido en Edeli Store.'} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={metaImage} />
      </Helmet>

      <DetailContainer active={isDarkMode} isLogged={!!token} isPublicPage={isPublicPage} isEmbedded={isEmbedded}>
        <BackButton onClick={onBack}>
          <span>←</span> Volver
        </BackButton>

        <MainGrid>
          <GallerySection>
            <FeaturedImageContainer
              active={isDarkMode}
              isVideo={content.type === 'video' || (Array.isArray(content.file) ? content.file[0] : content.file)?.toLowerCase()?.endsWith('.mp4') || (Array.isArray(content.file) ? content.file[0] : content.file)?.toLowerCase()?.endsWith('.mov')}
            >
              {renderMedia()}

              {(() => {
                const currentMedia = activeImage || (Array.isArray(content.file) ? content.file[0] : content.file) || content.image || '';
                const isVideo = typeof currentMedia === 'string' &&
                  (currentMedia.toLowerCase().endsWith('.mp4') ||
                  currentMedia.toLowerCase().endsWith('.mov') ||
                  currentMedia.toLowerCase().endsWith('.webm'));
                const isYoutube = content.type === 'video' && content.videoUrl;
                if (!isVideo && !isYoutube) {
                  return (
                    <ExpandButton onClick={() => setIsZoomOpen(true)} title="Ver en grande">
                      🔍
                    </ExpandButton>
                  );
                }
                return null;
              })()}
            </FeaturedImageContainer>

            {content.gallery && content.gallery.length > 0 && (
              <GalleryStrip>
                <GalleryStripThumb active={isDarkMode} onClick={() => setActiveImage(null)} selected={activeImage === null}>
                  {renderPrincipalThumb()}
                </GalleryStripThumb>
                {content.gallery.map((img, i) => (
                  <GalleryStripThumb
                    key={i} active={isDarkMode}
                    onClick={() => setActiveImage(getGalleryUrl(img))}
                    selected={activeImage === getGalleryUrl(img)}
                  >
                    <img src={getGalleryUrl(img)} alt={`gallery-${i}`} />
                  </GalleryStripThumb>
                ))}
              </GalleryStrip>
            )}
          </GallerySection>

          <InfoSection>
            <ProductTitle>{content.title}</ProductTitle>

            <PriceStockRow>
              {content.price && content.price !== 0 && content.price !== '0' ? (
                <PriceTag active={isDarkMode}>
                  <span className="currency">$</span>
                  {Number(content.price).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </PriceTag>
              ) : null}

              {content.stock != null && content.stock !== -1 && (
                <StockPill stock={content.stock}>
                  <StockPillDot stock={content.stock} />
                  {content.stock === 0 ? '🏆 100% vendido · Solicitar' : `${content.stock} Disponible`}
                </StockPill>
              )}
            </PriceStockRow>

            {!content.price || content.price === 0 || content.price === '0' ? (
              <div style={{ marginBottom: '20px' }} />
            ) : null}

            <MetaRow isDark={isDarkMode}>
              <MetaItem>
                <MetaIcon>👤</MetaIcon>
                <MetaLabel isDark={isDarkMode}>Creador</MetaLabel>
                <MetaValue isDark={isDarkMode}>{content.userId?.username || content.credits || 'Desconocido'}</MetaValue>
              </MetaItem>
              <MetaDivider isDark={isDarkMode} />
              <MetaItem>
                <MetaIcon>📅</MetaIcon>
                <MetaLabel isDark={isDarkMode}>Publicado</MetaLabel>
                <MetaValue isDark={isDarkMode}>
                  {new Date(content.createdAt).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </MetaValue>
              </MetaItem>
              <MetaDivider isDark={isDarkMode} />
              <MetaItem>
                <MetaIcon>📁</MetaIcon>
                <MetaLabel isDark={isDarkMode}>Tipo</MetaLabel>
                <MetaValue isDark={isDarkMode}>
                  {content.type === 'imagen' ? 'Imagen' : content.type === 'video' ? 'Video' : 'Archivo'}
                </MetaValue>
              </MetaItem>
            </MetaRow>

            <Label>DESCRIPCIÓN</Label>

            {/* ── DESCRIPCIÓN CON LINKS DETECTADOS AUTOMÁTICAMENTE ── */}
            <DescriptionBox isDark={isDarkMode}>
              {renderDescription(textToShow, isDarkMode)}
              
              {shouldTruncate && (
                <SeeMoreButton 
                  type="button" 
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? 'Ver menos' : 'Ver más'}
                </SeeMoreButton>
              )}
            </DescriptionBox>

            {content.isDigitalProduct && (
              <DownloadButton
                as="button"
                disabled={!canDownload()}
                canDownload={canDownload()}
                onClick={downloadFile}
              >
                {canDownload() ? '⬇ Descargar producto' : '🔒 Descarga bloqueada'}
              </DownloadButton>
            )}

            {(() => {
              const code = content.userId?.codeCountry || '';
              const number = content.userId?.celular || '';
              const full = (code + number).replace(/\D/g, '');
              const href = full
                ? `https://wa.me/${full}?text=${encodeURIComponent(`Hola, estoy interesado en el contenido: ${content.title}`)}`
                : null;
              return href ? (
                <WhatsAppButton href={href} target="_blank">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="ws" width="24" />
                  Contactar por WhatsApp
                </WhatsAppButton>
              ) : null;
            })()}

            <ShareWrapper>
              <ShareButtonDetail onClick={handleShare}>↗ Compartir</ShareButtonDetail>
              {shareMenuOpen && (
                <ShareMenu onClick={e => e.stopPropagation()}>
                  <ShareOption href={shareLinks.whatsapp} target="_blank" rel="noreferrer" color="#25D366">💬 WhatsApp</ShareOption>
                  <ShareOption href={shareLinks.telegram} target="_blank" rel="noreferrer" color="#0088cc">✈️ Telegram</ShareOption>
                  <ShareOption as="button" color="#aaa" onClick={() => { navigator.clipboard.writeText(shareLinks.url); alert('¡Enlace copiado!'); setShareMenuOpen(false); }}>
                    🔗 Copiar enlace
                  </ShareOption>
                </ShareMenu>
              )}
            </ShareWrapper>

            <AddToCartButton disabled>
              <span>+</span>
              Comprar
            </AddToCartButton>
            <small style={{ color: '#666', marginTop: '10px', textAlign: 'center' }}>
              Pagos automáticos disponibles próximamente.
            </small>
          </InfoSection>
        </MainGrid>

        {isZoomOpen && (
          <ZoomOverlay onClick={() => setIsZoomOpen(false)}>
            <ZoomContent onClick={e => e.stopPropagation()}>
              <CloseZoom onClick={() => setIsZoomOpen(false)}>✕</CloseZoom>
              <MediaWrapperInModal>
                {renderMedia()}
              </MediaWrapperInModal>
            </ZoomContent>
          </ZoomOverlay>
        )}
      </DetailContainer>
    </>
  );
};

// ═══════════════════════════════════════════
//  STYLED COMPONENTS
// ═══════════════════════════════════════════

// ── Link dentro de la descripción ──────────────────────────────────────────
const DescLink = styled.a`
  color: #a855f7;
  text-decoration: underline;
  word-break: break-all;
  transition: color 0.2s ease;
  &:hover { color: #c084fc; }
`;

const GalleryStrip = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap; width: 100%;
`;

const GalleryStripThumb = styled.div`
  width: 64px; height: 64px; border-radius: 8px; overflow: hidden;
  border: 2px solid ${p => p.selected ? '#a855f7' : p.active ? '#333' : '#eee'};
  cursor: pointer; transition: border-color 0.2s;
  img { width: 100%; height: 100%; object-fit: cover; }
  &:hover { border-color: #a855f7; }
`;

const DetailContainer = styled.div`
  background-color: ${props => props.active ? '#000' : '#fff'};
  color: ${props => props.active ? '#fff' : '#000'};
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;
  top: ${props => props.isLogged ? '0' : (window.innerWidth <= 600 ? '0' : '-44px')};
  min-height: auto;
  margin: ${props => props.isEmbedded ? '0' : props.isPublicPage ? '40px auto' : '80px'};
  padding: ${props => props.isEmbedded ? '5px 20px 20px 20px' : '5px 40px 20px 40px'};
  max-width: ${props => props.isPublicPage ? '1200px' : 'unset'};
  @media (max-width: 600px) {
    margin: 0; padding: 10px 20px 100px 20px;
    top: 0; margin-bottom: 0; display: block; overflow-x: hidden;
  }
`;

const BackButton = styled.button`
  background: none; border: none; color: #888; cursor: pointer;
  display: flex; align-items: center; gap: 10px;
  width: 100%; max-width: 1200px; margin: 0 auto;
  margin-top: -20px; margin-bottom: 25px; padding-left: 10px;
  font-size: 18px; font-weight: 600; transition: all 0.2s ease;
  span { font-size: 30px; }
  &:hover { color: #eee; transform: translateX(-5px); }
  @media (max-width: 768px) { margin-top: 20px; margin-bottom: 15px; font-size: 15px; span { font-size: 22px; } }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 40px;
  max-width: 1200px; width: 100%; margin: 0 auto; align-items: center;
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 20px; }
`;

const GallerySection = styled.div`
  width: 100%; display: flex; flex-direction: column;
  align-items: flex-start; gap: 10px;
`;

const FeaturedImageContainer = styled.div`
  position: relative;
  background: ${props => props.active ? '#111' : '#f4f4f4'};
  border: 1px solid ${props => props.active ? '#333' : '#eee'};
  border-radius: 12px; overflow: hidden; width: 100%;
  aspect-ratio: ${props => props.isVideo ? '16/9' : 'unset'};
  max-height: 60vh;
  display: flex; align-items: center; justify-content: center;
  img { width: auto; height: auto; max-width: 100%; max-height: 50vh; object-fit: contain; display: block; }
  @media (max-width: 900px) { max-height: 40vh; img { max-height: 40vh; } }
`;

const InfoSection = styled.div`display: flex; flex-direction: column;`;

const ProductTitle = styled.h1`
  font-size: 3rem; margin: 0 0 10px 0; font-weight: 700; line-height: 1.1;
  @media (max-width: 480px) { font-size: 2rem; }
`;

const PriceTag = styled.div`
  display: flex; align-items: center; gap: 4px;
  width: fit-content;
  margin-bottom: 0;  /* ← era 25px, ponlo en 0 porque ya lo maneja PriceStockRow */
  padding: 6px 15px;
  border-radius: 8px; font-weight: 800;
  background: ${props => props.active ? 'rgba(0,0,0,0.5)' : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : '#000000'};
  border: 2px solid #ffcc00;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1); font-size: 22px;
  .currency { color: #ffcc00; font-size: 21px; font-weight: 800; margin-right: 2px; }
  @media (max-width: 480px) { font-size: 18px; padding: 5px 12px; .currency { font-size: 17px; } }
`;

const Label = styled.label`
  font-size: 12px; color: #888; letter-spacing: 1px;
  margin-bottom: 10px; text-transform: uppercase;
`;

const WhatsAppButton = styled.a`
  background: #25D366; color: white; text-decoration: none;
  padding: 18px; border-radius: 40px; font-weight: bold;
  display: flex; justify-content: center; align-items: center;
  gap: 12px; margin-bottom: 15px; font-size: 16px; transition: all 0.2s ease;
  &:hover { background: #20ba5a; transform: scale(1.02); }
`;

const AddToCartButton = styled.button`
  background: ${props => props.disabled ? '#333' : '#2563eb'};
  color: ${props => props.disabled ? '#666' : 'white'};
  border: none; padding: 18px; border-radius: 40px; font-weight: bold;
  display: flex; justify-content: center; align-items: center; gap: 20px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  width: 100%; opacity: ${props => props.disabled ? '0.8' : '1'};
`;

const ShareWrapper = styled.div`position: relative; margin-bottom: 15px;`;

const ShareButtonDetail = styled.button`
  background: transparent; color: ${p => p.isDark ? '#555' : '#bbb'};
  border: 1px solid #a855f7; padding: 18px; border-radius: 40px;
  font-weight: bold; display: flex; justify-content: center;
  align-items: center; gap: 12px; width: 100%; font-size: 16px;
  cursor: pointer; transition: all 0.2s ease;
  &:hover { box-shadow: 0 0 8px #a855f7; color: #a855f7; }
`;

const ShareMenu = styled.div`
  position: absolute; bottom: 60px; left: 0; right: 0;
  background: #1e1e1e; border: 1px solid #333; border-radius: 10px;
  padding: 8px; display: flex; flex-direction: column; gap: 4px;
  z-index: 200; box-shadow: 0 8px 24px rgba(0,0,0,0.5);
`;

const ShareOption = styled.a`
  display: flex; align-items: center; gap: 8px; padding: 8px 12px;
  border-radius: 7px; font-size: 13px; font-weight: 600; color: #fff;
  text-decoration: none; background: transparent; border: none; cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover { background: ${p => p.color}22; color: ${p => p.color}; }
`;

const MetaRow = styled.div`display: flex; align-items: center; gap: 0; margin-bottom: 16px;`;
const MetaItem = styled.div`display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 10px 16px; flex: 1; text-align: center;`;
const MetaIcon = styled.span`font-size: 16px; line-height: 1;`;
const MetaLabel = styled.span`font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: ${p => p.isDark ? '#555' : '#bbb'};`;
const MetaValue = styled.span`font-size: 12px; font-weight: 700; color: ${p => p.isDark ? '#e0e0e0' : '#111'};`;
const MetaDivider = styled.div`width: 1px; height: 20px; background: ${p => p.isDark ? '#333' : '#ddd'}; flex-shrink: 0; margin: 0 4px;`;

const DescriptionBox = styled.div`
  border-left: 3px solid #a855f7; padding: 6px 0 6px 14px;
  font-size: 14px; line-height: 1.8;
  color: ${p => p.isDark ? '#e0e0e0' : '#333'};
  margin-bottom: 20px; font-style: italic; text-align: left;
`;

const DescriptionEmpty = styled.span`
  color: ${p => p.isDark ? '#444' : '#373737'}; font-style: italic; font-size: 13px;
`;

const DownloadButton = styled.a`
  background: ${p => p.canDownload ? 'linear-gradient(135deg, #4f46e5, #7c3aed)' : '#222'};
  color: ${p => p.canDownload ? 'white' : '#555'};
  border: 1px solid ${p => p.canDownload ? 'transparent' : '#333'};
  padding: 18px; border-radius: 40px; font-weight: bold;
  display: flex; justify-content: center; align-items: center;
  gap: 12px; margin-bottom: 15px; font-size: 16px; text-decoration: none;
  cursor: ${p => p.canDownload ? 'pointer' : 'not-allowed'}; transition: all 0.2s ease;
  &:hover { opacity: ${p => p.canDownload ? '0.88' : '1'}; transform: ${p => p.canDownload ? 'scale(1.02)' : 'none'}; }
`;

const ExpandButton = styled.button`
  position: absolute; top: 15px; right: 15px;
  background: #4d296f; color: white; border: none; border-radius: 50%;
  width: 40px; height: 40px; display: flex; align-items: center;
  justify-content: center; cursor: pointer; font-size: 18px;
  transition: all 0.2s; z-index: 10;
  &:hover { background: #9461c6; transform: scale(1.1); }
`;

const ZoomOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.9); display: flex; align-items: center;
  justify-content: center; z-index: 9999; padding: 20px;
  @media (max-width: 768px) { width: auto; }
`;

const ZoomContent = styled.div`
  position: relative; display: flex; align-items: center;
  justify-content: center; max-width: 95vw; max-height: 85vh; padding: 10px;
`;

const MediaWrapperInModal = styled.div`
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
  img { max-width: 100%; max-height: 85vh; object-fit: contain; border-radius: 8px; display: block; margin: 0 auto; box-shadow: 0 10px 30px rgba(0,0,0,0.5); }
  video, iframe { width: 85vw; aspect-ratio: 16/9; max-height: 80vh; }
`;

const CloseZoom = styled.button`
  position: absolute; top: -20px; left: 50%; transform: translateX(-50%);
  background: #a855f7; color: white; border: 2px solid white; border-radius: 50%;
  width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;
  font-size: 20px; font-weight: bold; cursor: pointer; z-index: 10001;
  box-shadow: 0 4px 15px rgba(0,0,0,0.5); transition: all 0.2s ease;
  &:hover { background: #9333ea; transform: translateX(-50%) scale(1.1); }
`;

const SeeMoreButton = styled.button`
  background: none;
  border: none;
  color: #a855f7;
  font-weight: bold;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
  display: inline-block;
  text-decoration: underline;
  transition: color 0.2s;

  &:hover {
    color: #c084fc;
  }
`;

const PriceStockRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 25px;
  flex-wrap: wrap;
`;

const StockPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.stock === 0 ? '#633806' : '#fff'};
  background: ${p => p.stock === 0 ? 'rgb(255 231 171 / 95%)' : 'rgba(22,163,74,0.92)'};
  border: 1.5px solid ${p => p.stock === 0 ? '#FAC775' : 'rgba(34,197,94,0.4)'};
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
`;

const StockPillDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.stock === 0 ? '#F59E0B' : '#fff'};
  opacity: ${p => p.stock === 0 ? '1' : '0.9'};
  box-shadow: 0 0 5px ${p => p.stock === 0 ? 'rgba(245,158,11,0.6)' : 'rgba(255,255,255,0.5)'};
  flex-shrink: 0;
`;

export default ProductDetailView;